/**
 * usePoseDetector — MediaPipe BlazePose wrapper with multi-pose support.
 *
 * Multi-pose (Feb 2026 upgrade) — the Shot Analyzer now needs to work with
 * multiple players in the frame (5v5 clips, group drills, defenders in
 * the background). We tell PoseLandmarker to detect up to `numPoses`
 * skeletons per frame; downstream code picks the "shooter" via ball
 * proximity or a manual tap.
 *
 * Public API:
 *   const { ready, error, start, stop, landmarksRef, landmarksListRef }
 *     = usePoseDetector({ numPoses: 4 });
 *   start(videoEl);                       // begin detection
 *   stop();                               // teardown
 *   landmarksListRef.current              // Array<Array<Landmark33>>
 *   landmarksRef.current                  // shortcut: first pose (legacy)
 */
import { useEffect, useRef, useState } from 'react';

const VISION_TASKS_VERSION = '0.10.35';
const WASM_ROOT = `https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@${VISION_TASKS_VERSION}/wasm`;
// `pose_landmarker_full` scales gracefully with multi-pose; `lite` only
// tracks one skeleton well. Full is ~10MB but cached after first load.
const MODEL_URL = 'https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_full/float16/latest/pose_landmarker_full.task';

export function usePoseDetector({ numPoses = 4 } = {}) {
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);
  const landmarkerRef = useRef(null);
  const rafRef = useRef(null);
  const videoRef = useRef(null);
  const landmarksRef = useRef(null);        // first pose (legacy shortcut)
  const landmarksListRef = useRef([]);      // all detected poses
  const lastTsRef = useRef(0);

  // Lazy-init the landmarker once.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { PoseLandmarker, FilesetResolver } = await import('@mediapipe/tasks-vision');
        const vision = await FilesetResolver.forVisionTasks(WASM_ROOT);
        if (cancelled) return;
        const lm = await PoseLandmarker.createFromOptions(vision, {
          baseOptions: { modelAssetPath: MODEL_URL, delegate: 'GPU' },
          runningMode: 'VIDEO',
          numPoses: Math.max(1, Math.min(numPoses, 6)),
        });
        if (cancelled) {
          lm.close?.();
          return;
        }
        landmarkerRef.current = lm;
        setReady(true);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load pose model');
      }
    })();
    return () => {
      cancelled = true;
      try { landmarkerRef.current?.close?.(); } catch { /* noop */ }
      landmarkerRef.current = null;
    };
  }, [numPoses]);

  const loop = () => {
    const video = videoRef.current;
    const lm = landmarkerRef.current;
    if (!video || !lm) return;
    const ts = performance.now();
    if (ts <= lastTsRef.current) lastTsRef.current = ts;
    lastTsRef.current = ts;
    try {
      const result = lm.detectForVideo(video, ts);
      const poses = result?.landmarks || [];
      landmarksListRef.current = poses;
      landmarksRef.current = poses[0] || null;
    } catch {
      // detectForVideo can throw transiently while the video buffer is
      // not ready — we just skip the frame.
    }
    rafRef.current = requestAnimationFrame(loop);
  };

  const start = (videoEl) => {
    videoRef.current = videoEl;
    if (!rafRef.current) rafRef.current = requestAnimationFrame(loop);
  };

  const stop = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    landmarksRef.current = null;
    landmarksListRef.current = [];
  };

  return { ready, error, start, stop, landmarksRef, landmarksListRef };
}
